import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Col, Row } from "reactstrap";
import TableComponent from "../../../components/TableComponent";
import Toast from "../../../helpers/Toast";
import { ClientDTO } from "../../../models/Client/ClientDTO";
import { ClientAddressDTO } from "../../../models/ClientAddress/ClientAdressDTO";
import { ClientAddressService } from "../../../services/ClientAddressService";
import { ClientService } from "../../../services/ClientService";
import { CountryService } from "../../../services/CountryService";
import ButtonComponent from "../../ButtonComponent";
import CreateAddressPopup from "../CreateAddressPopup";
import EditAddressPopup from "../EditAddressPopup";

type Props = {
    clientErpId: string;
    client?: ClientDTO | null;
};

export default function AddressesFromClientA({ clientErpId, ...props }: Props) {
    const intl = useIntl();
    const addressService = new ClientAddressService();
    const countryService = new CountryService();
    const clientService = new ClientService();

    const [data, setData] = useState<ClientAddressDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(1);

    const [popupVisible, setPopupVisible] = useState(false);
    const [editPopupVisible, setEditPopupVisible] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState<ClientAddressDTO | null>(null);
    const [client, setClient] = useState<ClientDTO | null>(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);

    useEffect(() => {
        if (props.client && props.client.id) {
            fetchClient();
        }
    }, []);

    useEffect(() => {
        if (props.client && props.client.id) {
            fetchData(currentPage, pageSize);
        }
    }, [currentPage, pageSize]);

    const fetchClient = async () => {
        if (!props.client || !props.client.id) return;
        try {
            const result = await clientService.GetClientById(props.client.id);

            if (result.success && result.obj) {
                setClient(result.obj);
            } else {
                Toast.Show("error", intl.formatMessage({ id: "Screens.ClientAddresses.ClientNotFound" }));
            }
        } catch {
            Toast.Show("error", intl.formatMessage({ id: "Screens.ClientAddresses.ClientNotFound" }));
        }
    };

    const getCountryName = async (countryId: number): Promise<string> => {
        try {
            const result = await countryService.GetCountryById(countryId);
            return result.success && result.obj ? result.obj.name : `País ID: ${countryId}`;
        } catch {
            return `País ID: ${countryId}`;
        }
    };

    const columns = [
        {
            Header: intl.formatMessage({ id: "Screens.ClientAddresses.Table.Address" }),
            accessor: "street1",
            width: 25,
        },
        {
            Header: intl.formatMessage({ id: "Screens.ClientAddresses.Table.AddressOptional" }),
            accessor: "street2",
            width: 20,
        },
        {
            Header: intl.formatMessage({ id: "Screens.ClientAddresses.Table.PostalCode" }),
            accessor: "postalCode",
            width: 10,
        },
        {
            Header: intl.formatMessage({ id: "Screens.ClientAddresses.Table.City" }),
            accessor: "city",
            width: 15,
        },
        {
            Header: intl.formatMessage({ id: "Screens.ClientAddresses.Table.Country" }),
            accessor: "countryName",
            width: 15,
        },
        {
            Header: intl.formatMessage({ id: "Screens.ClientAddresses.Table.Status" }),
            accessor: "active",
            width: 10,
            Cell: ({ value }: any) => (
                <span style={{ color: value ? "green" : "red", fontWeight: "bold" }}>
                    {value ? "Ativo" : "Inativo"}
                </span>
            ),
        },
        {
            Header: intl.formatMessage({ id: "Common.Buttons.Edit" }),
            accessor: "id",
            width: 10,
            Cell: ({ row }: any) => (
                <ButtonComponent
                    onClick={() => handleEdit(row.original)}
                    text={intl.formatMessage({ id: "Common.Buttons.Edit" })}
                    style={{ padding: "4px 8px", minWidth: "unset" }}
                />
            ),
        },
    ];

    const fetchData = async (page: number, pageSize: number) => {
        if (!props.client || !props.client.id) return;
        setLoading(true);
        try {
            const result = await addressService.getAllByClientId(props.client.id);
            if (!result.success || !Array.isArray(result.obj)) {
                Toast.Show("error", intl.formatMessage({ id: "Screens.ClientAddresses.Table.Error" }));
                setData([]);
                setPageCount(0);
            } else {
                const allData = result.obj;

                const enrichedData = await Promise.all(
                    allData.map(async (item: ClientAddressDTO) => {
                        const countryName = item.countryId
                            ? await getCountryName(item.countryId)
                            : "Sem país";
                        return { ...item, countryName };
                    })
                );

                const start = (page - 1) * pageSize;
                const paginatedData = enrichedData.slice(start, start + pageSize);
                setData(paginatedData);
                setPageCount(Math.ceil(enrichedData.length / pageSize));
            }
        } catch {
            Toast.Show("error", intl.formatMessage({ id: "Screens.ClientAddresses.ErrorFetching" }));
            setData([]);
            setPageCount(0);
        }
        setLoading(false);
    };

    const handleEdit = (address: ClientAddressDTO) => {
        setSelectedAddress(address);
        setEditPopupVisible(true);
    };

    const handleCreate = () => setPopupVisible(true);

    const handlePopupClose = () => {
        setPopupVisible(false);
        fetchData(1, pageSize);
    };

    const handleEditPopupClose = () => {
        setEditPopupVisible(false);
        setSelectedAddress(null);
    };

    const handleEditPopupUpdated = () => {
        setEditPopupVisible(false);
        setSelectedAddress(null);
        fetchData(1, pageSize);
    };

    return (
        <Row style={{ width: "100%", height: "100%" }}>
            <Col xs="12" className="mb-3" style={{ display: "flex", justifyContent: "flex-start" }}>
                <ButtonComponent
                    text={intl.formatMessage({ id: "Screens.ClientAddresses.Create" })}
                    onClick={handleCreate}
                    style={{ minWidth: "100px" }}
                />
            </Col>

            <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, width: "100%" }}>
                <TableComponent
                    columns={columns}
                    data={data}
                    fetchData={fetchData}
                    loading={loading}
                    manualPagination={true}
                    pageCount={pageCount}
                    initialPage={currentPage}
                    initialPageSize={pageSize}
                    pageSizes={[5, 15, 30]}
                    initialSortBy={[]}
                    goToPage={undefined}
                />
            </div>

            <CreateAddressPopup
                visible={popupVisible}
                onClose={handlePopupClose}
                onCreated={handleCreate}
                client={client}
            />

            <EditAddressPopup
                visible={editPopupVisible}
                onClose={handleEditPopupClose}
                address={selectedAddress}
                onUpdated={handleEditPopupUpdated}
            />
        </Row>
    );
}
